import { memo } from 'react';
import PropTypes from 'prop-types';

import { CircularProgress } from '@mui/material';
import { compose } from 'redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinksContainers from 'components/LinksContainers';
import ButtonCard from '@experimental-components/ButtonCard';
import TitleSection from 'components/TitleSection';
import dayjs from 'dayjs';
import { useQueryParams } from 'hooks';
import { useHistory, Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

import { Feature, useFeature } from 'FeatureProvider';

import { withRequiredLicense } from 'providers/root/AuthProvider';

import { useStoreSettings } from 'providers/settings/Common/hooks';

import { reportsTypes } from 'utils/reports';
import { useGetEnableGeneralFlag, useHiddenLegacyReports } from 'utils/featureFlags/reports';

import { useCashOutsUrl } from 'containers/ReportCashRegister/hooks';
import { useCommandBarReports } from 'hooks/useCommandBarAttributes';
import { isInventoryMfeEnabled } from 'mfe/remotes/inventory';
import { REPORT_PATHS } from 'domains/reports/presentation/utils';

import LayoutReport from './LayoutReport';

function Reports({ t }) {
  const history = useHistory();
  const theme = useTheme();
  const { queryParams } = useQueryParams();
  const { data: settingsStore, isLoading, isSuccess: settingsStoreSuccess } = useStoreSettings();
  const commandBarHook = useCommandBarReports();
  const { baseUrl: cashOutsBaseUrl } = useCashOutsUrl();
  const enableAverageTicketReport = useGetEnableGeneralFlag('ffEnableAverageTicketReport');
  const enableReportsVersion2 = useGetEnableGeneralFlag('ffEnableReportsVersion2');
  const enableProductsReportVersion2 = useGetEnableGeneralFlag('ffEnableProductsReportVersion2');
  const hasInventory = useFeature('@module.inventory');
  const hiddenReports = useHiddenLegacyReports();
  // The report reads the V1 inventories service, so it has nothing to show once the org is served V2.
  const showCostsAndMargins = !hiddenReports.costsAndMargins && !isInventoryMfeEnabled();

  const handleGoRoute = (route, query) => () => {
    // if (query?.startDate && query?.endDate) {
    //   history.push({
    //     pathname: route,
    //     search: `?${new URLSearchParams(query).toString()}`,
    //   });
    //   return;
    // }

    history.push(route);
  };

  function createQueryParamDates({ startDate, endDate }) {
    if (!startDate || !endDate) {
      return undefined;
    }

    return {
      startDate: dayjs(decodeURI(startDate)).format('YYYY-MM-DDTHH:mm:ss'),
      endDate: dayjs(decodeURI(endDate)).format('YYYY-MM-DDTHH:mm:ss'),
    };
  }

  return (
    <LayoutReport path="/reports">
      <Box pb={theme.typography.pxToRem(32)} pl={theme.typography.pxToRem(32)} pr={theme.typography.pxToRem(32)}>
        <LinksContainers>
          <TitleSection
            color="primary.main"
            commandBarHook={commandBarHook}
            component="h1"
            fontSize={theme.typography.pxToRem(24)}
            fontWeight={500}
            title={t('reports:index.title')}
            wrapperProps={{ mb: 3, pt: 4, pl: 0, pr: 0 }}
          />

          {(enableReportsVersion2 || enableProductsReportVersion2) && (
            <Typography component="h2" mb={2} pl={0} pr={0} pt={1} variant="h6">
              {t('reports:index.AllReports')}
            </Typography>
          )}

          <LinksContainers.Row>
            {!hiddenReports.productsSales && (
              <LinksContainers.Item>
                <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.PRODUCTS_SALES}/summary`)}>
                  {t('reports:navigation.tabProductsSales')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.salesPerCategory && (
              <LinksContainers.Item>
                <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.SALES_PER_CATEGORY}`)}>
                  {t('reports:navigation.tabSalesPerCategory')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {enableAverageTicketReport && !hiddenReports.averageTicket && (
              <LinksContainers.Item>
                <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.AVERAGE_TICKET}/summary`)}>
                  {t('reports:navigation.averageTicketReport')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.cashRegister && (
              <LinksContainers.Item>
                <ButtonCard component={Link} to={cashOutsBaseUrl}>
                  {t('reports:navigation.tabCashRegister')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.orders && (
              <LinksContainers.Item>
                <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.ORDERS}/summary`)}>
                  {t('reports:navigation.tabOrders')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.payments && (
              <LinksContainers.Item>
                <ButtonCard
                  onClick={handleGoRoute(`/reports/${reportsTypes.PAYMENTS}/summary`, {
                    ...createQueryParamDates({
                      startDate: queryParams.startDate,
                      endDate: queryParams.endDate,
                    }),
                    status: queryParams?.status,
                  })}
                >
                  {t('reports:navigation.tabPayments')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.salesPerTime && (
              <LinksContainers.Item onClick={handleGoRoute(`/reports/${reportsTypes.SALES_PER_TIME}`)}>
                <ButtonCard>{t('reports:navigation.salesPerTime')}</ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.modifiers && (
              <LinksContainers.Item>
                <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.MODIFIERS}/modifiersByProduct`)}>
                  {t('reports:navigation.modifiersReport')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.cancellations && (
              <LinksContainers.Item>
                <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.CANCELLATIONS}/summary`)}>
                  {t('reports:navigation.tabCancellations')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.discounts && (
              <LinksContainers.Item>
                <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.DISCOUNTS}/summary`)}>
                  {t('reports:navigation.tabDiscounts')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.clientData && (
              <LinksContainers.Item>
                <ButtonCard
                  data-testid="client-data-tab"
                  onClick={handleGoRoute(`/reports/${reportsTypes.CLIENT_DATA}/summary`)}
                >
                  {t('reports:navigation.tabClientData')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.users && (
              <LinksContainers.Item>
                <ButtonCard data-testid="users-tab" onClick={handleGoRoute(`/reports/${reportsTypes.USERS}`)}>
                  {t('reports:navigation.reportUsers')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            <Feature name="@module.timeClock">
              {!hiddenReports.assistance && (
                <LinksContainers.Item>
                  <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.ASSISTANCE}`)}>
                    {t('reports:navigation.reportAssistance')}
                  </ButtonCard>
                </LinksContainers.Item>
              )}
            </Feature>

            {showCostsAndMargins &&
              (hasInventory ? (
                <LinksContainers.Item>
                  <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.COSTS_AND_MARGINS}`)}>
                    {t('reports:navigation.reportCostsAndMargins')}
                  </ButtonCard>
                </LinksContainers.Item>
              ) : (
                <LinksContainers.Item>
                  <ButtonCard disabledAction tooltipTitle="Contrata inventarios para ver este reporte">
                    {t('reports:navigation.reportCostsAndMargins')}
                  </ButtonCard>
                </LinksContainers.Item>
              ))}

            {!hiddenReports.movementsCash && (
              <LinksContainers.Item>
                <ButtonCard onClick={handleGoRoute(`/reports/${reportsTypes.MOVEMENTS_CASH}`)}>
                  {t('reports:navigation.movementsCash')}
                </ButtonCard>
              </LinksContainers.Item>
            )}

            {!hiddenReports.transactionsPay &&
              (isLoading ? (
                <LinksContainers.Item>
                  <ButtonCard>
                    <CircularProgress color="inherit" size={18} />
                  </ButtonCard>
                </LinksContainers.Item>
              ) : (
                settingsStoreSuccess &&
                settingsStore?.parrotPay?.isEnabled && (
                  <LinksContainers.Item data-cy="transactionsPayItem">
                    <ButtonCard
                      onClick={handleGoRoute(`/reports/${reportsTypes.TRANSACTIONS_PAY}`, {
                        ...createQueryParamDates({
                          startDate: queryParams.startDate,
                          endDate: queryParams.endDate,
                        }),
                        status: queryParams?.status,
                      })}
                    >
                      {t('reports:navigation.transactionsPay')}
                    </ButtonCard>
                  </LinksContainers.Item>
                )
              ))}
          </LinksContainers.Row>

          {(enableReportsVersion2 || enableProductsReportVersion2) && (
            <>
              <Typography component="h2" fontWeight={500} mb={2} pl={0} pr={0} pt={4} variant="h6">
                {t('reports:index.newReports')}
              </Typography>

              <LinksContainers.Row>
                {enableReportsVersion2 && (
                  <LinksContainers.Item>
                    <ButtonCard onClick={handleGoRoute(REPORT_PATHS.SALES)}>
                      {t('reports:navigation.tabProductsSales')}
                    </ButtonCard>
                  </LinksContainers.Item>
                )}
                {enableReportsVersion2 && (
                  <LinksContainers.Item>
                    <ButtonCard onClick={handleGoRoute(REPORT_PATHS.PAYMENTS)}>
                      {t('reports:navigation.tabPayments')}
                    </ButtonCard>
                  </LinksContainers.Item>
                )}
                {enableProductsReportVersion2 && (
                  <LinksContainers.Item>
                    <ButtonCard onClick={handleGoRoute(REPORT_PATHS.PRODUCTS)}>
                      {t('reports:navigation.tabProducts')}
                    </ButtonCard>
                  </LinksContainers.Item>
                )}
                {enableReportsVersion2 && (
                  <LinksContainers.Item>
                    <ButtonCard onClick={handleGoRoute(REPORT_PATHS.CANCELLATION)}>
                      {t('reports:navigation.tabCancellations')}
                    </ButtonCard>
                  </LinksContainers.Item>
                )}
              </LinksContainers.Row>
            </>
          )}
        </LinksContainers>
      </Box>
    </LayoutReport>
  );
}

Reports.propTypes = {
  t: PropTypes.func,
};

export default compose(memo, withRequiredLicense(), withTranslation('reports'))(Reports);
